import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import {
  DataSourceDisplayComponent,
  PreSelectedFiltersComponent,
} from '@principle-theorem/ng-reporting';
import {
  CustomTooltipModule,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import { StateBasedNavigationService } from '@principle-theorem/ng-principle-shared';
import {
  ICustomReport,
  IReportRun,
} from '@principle-theorem/principle-core/interfaces';
import { OrganisationLinks } from '@principle-theorem/principle-core';
import {
  REPORT_BUILDER_DATA_SOURCES,
  ReportBuilderDataSourceInstance,
  resolveDataSource,
} from '@principle-theorem/reporting';
import {
  DocumentReference,
  Firestore,
  isWithRef,
  WithRef,
} from '@principle-theorem/shared';
import { of, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

@Component({
  selector: 'pr-campaign-report-summary',
  templateUrl: './campaign-report-summary.component.html',
  styleUrl: './campaign-report-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-4' },
  imports: [
    NgCommonModule,
    RouterModule,
    NgMaterialModule,
    DataSourceDisplayComponent,
    PreSelectedFiltersComponent,
    CustomTooltipModule,
  ],
})
export class CampaignReportSummaryComponent {
  private _stateNav = inject(StateBasedNavigationService);
  customReport = input.required<ICustomReport | WithRef<ICustomReport>>();
  reportRunRef = input<DocumentReference<IReportRun>>();
  dataSource = computed(() => this._getDataSource(this.customReport()));
  reportLink = computed(() => {
    const report = this.customReport();
    return isWithRef(report)
      ? ['/reporting', 'custom-reports', report.ref.id]
      : undefined;
  });

  reportRunExists = toSignal(
    toObservable(this.reportRunRef).pipe(
      switchMap((ref) => {
        if (!ref) {
          return of(false);
        }
        return from(Firestore.docExists(ref)).pipe(catchError(() => of(false)));
      })
    ),
    { initialValue: false }
  );

  async viewRunResults(): Promise<void> {
    const runRef = this.reportRunRef();
    if (!runRef) {
      return;
    }
    const link = OrganisationLinks.reportRunHistory(runRef);
    await this._stateNav.organisation(link.segments);
  }

  private _getDataSource(
    customReport: ICustomReport
  ): ReportBuilderDataSourceInstance | undefined {
    return resolveDataSource(
      customReport.dataSource,
      REPORT_BUILDER_DATA_SOURCES
    );
  }
}
